/**
 * C.14 — Register-as-Skill dialog. Shown when the user clicks the
 * "Register as Skill" banner on a Skills/<name>/SKILL.md note. Reads
 * name + description from the note's frontmatter (pre-validated by the
 * banner) and confirms before calling the Convex mutation.
 */

import { useState } from 'react';
import { useMutation } from 'convex/react';
import { useUser } from '@clerk/react';
import { api } from '../../../convex/_generated/api';
import { useResolvedPortalSlug } from '../../lib/tenant/portalSlugFromUrl';
import { useShowcaseVisible } from '../../lib/showcase/useShowcaseVisible';
import { useShowcaseSessionId } from '../../lib/showcase/showcaseSession';
import { testIds } from '../../lib/testIds';

export interface RegisterAsSkillDialogProps {
  vaultPath: string;
  initialName: string;
  initialDescription: string;
  body: string;
  onClose: () => void;
  onSuccess?: () => void;
}

export function RegisterAsSkillDialog(props: RegisterAsSkillDialogProps) {
  const [demo] = useShowcaseVisible();
  const showcaseSessionId = useShowcaseSessionId();
  const { user } = useUser();
  const register = useMutation(api.skills.registerFromVaultNote);

  const [name, setName] = useState(props.initialName);
  const [description, setDescription] = useState(props.initialDescription);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const clientSlug = useResolvedPortalSlug() ?? 'demo';
  const actorUserId = demo
    ? `demo:${showcaseSessionId.slice(0, 12)}`
    : (user?.id ?? 'unknown');

  async function submit() {
    setError(null);
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    if (!description.trim()) {
      setError('Description is required.');
      return;
    }
    setSubmitting(true);
    try {
      await register({
        clientSlug,
        demoSessionId: demo ? showcaseSessionId : undefined,
        vaultPath: props.vaultPath,
        name: name.trim(),
        description: description.trim(),
        body: props.body,
        frontmatter: { name: name.trim(), description: description.trim() },
        actorUserId,
      });
      props.onSuccess?.();
      props.onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
      }}
    >
      <div
        data-testid={testIds.obsidianVault.registerSkillDialog}
        style={{
          background: '#ece9d8',
          border: '2px solid #fff',
          borderRightColor: '#808080',
          borderBottomColor: '#808080',
          padding: 16,
          width: 480,
          color: '#000',
        }}
      >
        <h3 style={{ margin: 0, marginBottom: 8 }}>Register as Skill</h3>
        <p style={{ fontSize: 12, margin: '4px 0 8px' }}>
          From <code>{props.vaultPath}</code>
        </p>
        <label htmlFor="reg-name" style={{ display: 'block', fontSize: 11, marginTop: 6 }}>
          Skill name
        </label>
        <input
          id="reg-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={{ width: '100%', padding: 4 }}
        />
        <label htmlFor="reg-desc" style={{ display: 'block', fontSize: 11, marginTop: 6 }}>
          Description
        </label>
        <textarea
          id="reg-desc"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          style={{ width: '100%', padding: 4, resize: 'vertical' }}
        />
        {error && (
          <div
            style={{
              marginTop: 8,
              color: '#a00',
              background: '#fff0f0',
              border: '1px solid #d99',
              padding: 6,
              fontSize: 11,
            }}
          >
            {error}
          </div>
        )}
        <div style={{ marginTop: 12, display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
          <button
            type="button"
            data-testid={testIds.obsidianVault.registerSkillCancel}
            onClick={props.onClose}
            disabled={submitting}
            style={{ padding: '3px 12px' }}
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid={testIds.obsidianVault.registerSkillSubmit}
            onClick={() => void submit()}
            disabled={submitting}
            style={{ padding: '3px 12px' }}
          >
            {submitting ? 'Registering…' : 'Register'}
          </button>
        </div>
      </div>
    </div>
  );
}
